// Shared lists, projects and areas on the web (docs/43; the iPhone's SharingViews.swift): who a list is shared with,
// under its name; the people in it, and leaving; invitations on Home, and joining one. Inviting people and stopping a
// share stay in the apps for now (docs/47); the web says so where the apps would offer it.

import { useState } from "react";
import type { Route } from "../core/lists";
import type { Invitation } from "../data/invitations";
import { ShareError, type ShareFailure } from "../data/shareService";
import { currentLanguage, key, t } from "../i18n";
import { useWindow } from "./context";
import { splitEmoji } from "./format";
import { Dialog } from "./Sheets";
import type { SharedInfo } from "./model";

const FAILURES: Record<ShareFailure, string> = {
  needsPro: key("Sharing is part of Todolix Pro."),
  full: key("Ten people at most can be in one."),
  expired: key("This invitation has run out. Ask for a new one."),
  notAllowed: key("Only the owner can do that."),
  unreachable: key("Todolix could not be reached. Try again."),
  unverified: key("Confirm your email address first: there is a mail from Todolix in your inbox."),
  limit: key("That is enough invitations for today. Try again tomorrow."),
  gone: key("This share has ended."),
  failed: key("Todolix could not be reached. Try again."),
};

export function failureText(error: unknown): string {
  return t(FAILURES[error instanceof ShareError ? error.failure : "unreachable"]);
}

/** A person's initial in a small circle: quiet, the same for everyone. */
export function PersonBadge({ name, size = 22 }: { name: string; size?: number }) {
  return (
    <span className="person-badge" style={{ width: size, height: size, fontSize: size * 0.5 }} aria-hidden="true">
      {(name.trim()[0] ?? "?").toUpperCase()}
    </span>
  );
}

/** "Shared with Anna and Ben": everyone but the person looking. */
function sharedWithText(share: SharedInfo): string {
  const names = share.people.filter((person) => !person.isYou).map((person) => person.name);
  if (names.length === 0) return t("Shared, waiting for others to join");
  const list = new Intl.ListFormat(currentLanguage(), { style: "long", type: "conjunction" }).format(names);
  return t("Shared with {names}", { names: list });
}

/** Under a shared list's, project's or area's name: who it is shared with. A click shows the people. */
export function SharedWithLine({ route }: { route: Route }) {
  const { model, showSheet } = useWindow();
  const share = model.shareOf(route);
  if (!share) return null;
  return (
    <button className="shared-with" onClick={() => showSheet({ kind: "people", route })}>
      <span className="badges">{share.people.slice(0, 4).map((person) => <PersonBadge key={person.uid} name={person.name} size={24} />)}</span>
      <span>{sharedWithText(share)}</span>
    </button>
  );
}

/** Who is in a share; a member can leave. Inviting and stopping are in the apps for now. */
export function PeopleSheet({ route, onClose }: { route: Route; onClose: () => void }) {
  const { model, showToast, go } = useWindow();
  const share = model.shareOf(route);
  const [asks, setAsks] = useState(false);
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  if (!share) return null;
  const title = share.title || "";
  const leave = async () => {
    setWorking(true);
    setProblem(null);
    try {
      await model.sharing.leave(share.id);
      onClose();
      go({ kind: "today" });
      showToast(t("You left “{name}”", { name: title }));
    } catch (error) {
      setProblem(failureText(error));
    }
    setWorking(false);
  };
  return (
    <Dialog onClose={onClose}>
      <h2 id="sheet-title">{t("Shared “{name}”", { name: title })}</h2>
      <h3 className="sheet-label">{t("People")}</h3>
      <ul className="people">
        {share.people.map((person) => (
          <li key={person.uid}>
            <PersonBadge name={person.name} size={32} />
            <span className="person-text">
              <b>{person.isYou ? t("{name} (you)", { name: person.name }) : person.name}</b>
              <span>{person.email}</span>
            </span>
            {person.isOwner && <span className="person-role">{t("Owner")}</span>}
          </li>
        ))}
      </ul>
      <p className="sheet-note">
        {share.youOwn
          ? t("Invite people, or stop sharing, in the Todolix app on your iPhone, iPad or Mac.")
          : t("Everyone in it can add, change and complete its to-dos. Its name, sections and place are the owner's.")}
      </p>
      {problem && <p className="sheet-problem" role="alert">{problem}</p>}
      {asks ? (
        <div className="confirm-inline">
          <p><b>{t("Leave “{name}”?", { name: title })}</b> {t("It goes from your Todolix. The owner keeps it.")}</p>
          <div className="actions">
            <button className="outline-button" onClick={() => setAsks(false)}>{t("Cancel")}</button>
            <button className="mint-button destructive" disabled={working} onClick={() => void leave()}>{t("Leave")}</button>
          </div>
        </div>
      ) : (
        <div className="actions">
          {!share.youOwn && <button className="outline-button destructive" onClick={() => setAsks(true)}>{t("Leave…")}</button>}
          <button className="mint-button" onClick={onClose}>{t("Done")}</button>
        </div>
      )}
    </Dialog>
  );
}

// MARK: - Invitations

/** On Home, above the lists: each invitation with Join and Not Now (out of the way until the page loads again). */
export function InvitationCards() {
  const { state, showSheet, model, showToast } = useWindow();
  const [later, setLater] = useState<ReadonlySet<string>>(new Set());
  const waiting = state.invitations.filter((invitation) => !later.has(invitation.id));
  if (waiting.length === 0) return null;
  const decline = async (invitation: Invitation) => {
    try {
      await model.sharing.decline(invitation.id);
    } catch (error) {
      showToast(failureText(error));
    }
  };
  return (
    <div className="invitations">
      {waiting.map((invitation) => (
        <section key={invitation.id} className="invitation" aria-label={invitation.title}>
          <div className="invitation-top">
            <PersonBadge name={invitation.fromName || "?"} size={36} />
            <div>
              <b>{invitation.fromName
                ? t("{name} invites you to “{title}”", { name: invitation.fromName, title: invitation.title })
                : t("An invitation to “{title}”", { title: invitation.title })}</b>
              <span>{invitation.kind === "area"
                ? t("A shared area: you can add, change and complete its to-dos.")
                : t("A shared list: you can add, change and complete its to-dos.")}</span>
            </div>
          </div>
          <div className="invitation-actions">
            <button className="mint-button small" onClick={() => showSheet({ kind: "join", invitationID: invitation.id })}>{t("Join")}</button>
            <button className="outline-button small" onClick={() => setLater((current) => new Set([...current, invitation.id]))}>{t("Not Now")}</button>
            <button className="link quiet" onClick={() => void decline(invitation)}>{t("Decline")}</button>
          </div>
        </section>
      ))}
    </div>
  );
}

/** Which area a shared list or project goes into for this person; a shared area needs none. */
export function JoinSheet({ invitationID, onClose }: { invitationID: string; onClose: () => void }) {
  const { state, model, showToast } = useWindow();
  const invitation = state.invitations.find((one) => one.id === invitationID);
  // Not into an area that is shared itself: that would put the list into two shares.
  const areas = state.lists.sortedAreas.filter((area) => model.shareOf({ kind: "area", id: area.id }) === null);
  const [chosen, setChosen] = useState<string | undefined>(areas[0]?.id);
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  if (!invitation) return null;
  const isArea = invitation.kind === "area";
  const join = async () => {
    setWorking(true);
    setProblem(null);
    try {
      await model.sharing.accept(invitation.id, isArea ? undefined : chosen);
      onClose();
      showToast(t("You joined “{name}”", { name: invitation.title }));
    } catch (error) {
      setProblem(failureText(error));
      setWorking(false);
    }
  };
  return (
    <Dialog onClose={onClose}>
      <h2 id="sheet-title">{t("Join “{name}”", { name: invitation.title })}</h2>
      {isArea ? (
        <p>{t("The area “{name}” joins your areas, with everything in it.", { name: invitation.title })}</p>
      ) : (
        <div className="use-field">
          <span id="join-area">{t("Put It In")}</span>
          <div className="area-choices" role="radiogroup" aria-labelledby="join-area">
            {areas.map((area) => {
              const { emoji, name } = splitEmoji(area.title);
              return (
                <button key={area.id} role="radio" aria-checked={chosen === area.id} className={`area-choice${chosen === area.id ? " chosen" : ""}`}
                  onClick={() => setChosen(area.id)}>
                  {[emoji, name].filter(Boolean).join(" ")}
                </button>
              );
            })}
          </div>
          <p className="use-note">{t("“{title}” shows in this area for you; for {name} it stays where they keep it.", { title: invitation.title, name: invitation.fromName || t("them") })}</p>
        </div>
      )}
      {problem && <p className="sheet-problem" role="alert">{problem}</p>}
      <div className="actions">
        <button className="outline-button" onClick={onClose}>{t("Cancel")}</button>
        <button className="mint-button" disabled={working || (!isArea && !chosen)} onClick={() => void join()}>{t("Join")}</button>
      </div>
    </Dialog>
  );
}
